import { BUSINESS_PLAN_TYPES } from 'constants/businessPlan';
import { FOR_BANK_SECTIONS, FOR_BANK_SUB_SECTIONS } from 'constants/businessToolSections/forBank';
import { FOR_INVESTORS_SECTIONS, FOR_INVESTORS_SUB_SECTIONS } from 'constants/businessToolSections/forInvestors';
import { ONE_PAGE_SECTIONS } from 'constants/businessToolSections/onePage';
import { ONE_PAGE_SHORT_SECTIONS } from 'constants/businessToolSections/onePageShort';
import dayjs from 'dayjs';
import { ONE_PAGE_STYLE_OPTIONS } from 'layouts/OnePagePreviewLayout';
import { useUserStore } from 'store/useUserStore';
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

import { DOCUMENT_STYLE_TYPES } from 'pages/BusinessToolPage/components/DocumentStyleSelector/constant';

import { IBusinessPlanEntity } from 'interfaces/businessPlan';

import { OPTION_TYPES } from 'components/_basic/ColorSelector/constants';
import { GLOBAL_FIELD_NAMES } from 'components/BusinessToolForm/Sections/Global/form';
import { initialValues } from 'components/BusinessToolForm/utils/form';

import { IBusinessToolStore } from './types';

const DEFAULT_TITLE = 'Document 1';

export const DEFAULT_METADATA = {
  isFirstTryToSetBusinessToolTitle: true,
  pdfDocShortLink: '',
  pdfShareLink: '',
  pdfCreatedAt: '',
  onePageTemplateStyle: ONE_PAGE_STYLE_OPTIONS.FIRST_STYLE,
  documentColorType: OPTION_TYPES.GREEN,
  documentStyleType: DOCUMENT_STYLE_TYPES.SAMPLE_ONE,
  isShownAIGenerateWholeDocument: false,
  isHiddenFillOutCard: false,
  isGeneratedWholeDocument: false,
  aiGeneratedSections: [],
  aiGeneratedFields: [],
  updateOnlinePDFCount: 0,
  downloadDocumentCount: 0,
};

export const getDefaultBusinessPlan = (): IBusinessPlanEntity => {
  return {
    uuid: '',
    title: DEFAULT_TITLE,
    type: BUSINESS_PLAN_TYPES.FOR_BANK,
    status: 'DRAFT',
    metadata: DEFAULT_METADATA,
    content: {},
    createdAt: dayjs().toISOString(),
    updatedAt: dayjs().toISOString(),
    createdBy: '',
    viewonly: false,
  };
};

export const useBusinessToolStore = create<IBusinessToolStore>()(
  devtools(
    persist(
      (set, get) => ({
        businessPlan: getDefaultBusinessPlan(),
        setBusinessPlan: (businessPlan) => {
          set({
            businessPlan,
            businessPlanTitle: businessPlan.title,
            businessPlanType: businessPlan.type,
            isFirstTryToSetBusinessToolTitle: businessPlan.metadata.isFirstTryToSetBusinessToolTitle ?? true,
          });
        },

        activeSection: FOR_BANK_SECTIONS.OVERVIEW,
        setActiveSection: (section) => set({ activeSection: section }),

        activeSubSection: FOR_BANK_SUB_SECTIONS.FOR_BANK_OVERVIEW.COMPANY,
        setActiveSubSection: (subSection) => set({ activeSubSection: subSection }),

        businessPlanChapters: initialValues,
        setBusinessPlanChapter: (chapterName, chapterContent) => {
          const isUserAuth = useUserStore.getState().authUser;

          set({
            businessPlanChapters: {
              ...get().businessPlanChapters,
              [chapterName]: chapterContent,
            },
          });
          if (!isUserAuth) {
            set({ businessPlan: { ...get().businessPlan, updatedAt: dayjs().toISOString() } });
          }
        },
        setBusinessPlanChapters: (chapters) => set({ businessPlanChapters: chapters }),
        getBusinessToolChapter: (chapterName) => get().businessPlanChapters[chapterName],

        getBusinessPlanRequiredFields: (type) => {
          if (type === BUSINESS_PLAN_TYPES.ONE_PAGE_SHORT) {
            return [
              GLOBAL_FIELD_NAMES.COMPANY_NAME,
              GLOBAL_FIELD_NAMES.COMPANY_BRIEF_DESCRIPTION,
              GLOBAL_FIELD_NAMES.PROJECT_BRIEF_DESCRIPTION,
            ];
          }

          if (type === BUSINESS_PLAN_TYPES.ONE_PAGE) {
            return [
              GLOBAL_FIELD_NAMES.COMPANY_NAME,
              GLOBAL_FIELD_NAMES.ADDRESS,
              GLOBAL_FIELD_NAMES.COMPANY_BRIEF_DESCRIPTION,
              GLOBAL_FIELD_NAMES.PROJECT_BRIEF_DESCRIPTION,
            ];
          }

          return [
            GLOBAL_FIELD_NAMES.COMPANY_NAME,
            GLOBAL_FIELD_NAMES.ADDRESS,
            GLOBAL_FIELD_NAMES.COMPANY_BRIEF_DESCRIPTION,
            GLOBAL_FIELD_NAMES.PROJECT_BRIEF_DESCRIPTION,
          ];
        },

        businessPlanTitle: DEFAULT_TITLE,
        isFirstTryToSetBusinessToolTitle: true,
        setBusinessPlanTitle: (title) => {
          const newTitle = title ?? DEFAULT_TITLE;
          const isUserAuth = useUserStore.getState().authUser;

          set({
            businessPlanTitle: newTitle,
            businessPlan: {
              ...get().businessPlan,
              title: newTitle,
              ...(!isUserAuth ? { updatedAt: dayjs().toISOString() } : {}),
            },
          });
          if (get().isFirstTryToSetBusinessToolTitle) {
            set({ isFirstTryToSetBusinessToolTitle: false });
          }
        },
        businessPlanType: BUSINESS_PLAN_TYPES.FOR_BANK,
        setBusinessPlanType: (type) => {
          const updates: Partial<IBusinessToolStore> = {
            businessPlanType: type,
            businessPlan: { ...get().businessPlan, type },
            activeSection: undefined,
            activeSubSection: undefined,
          };

          switch (type) {
            case BUSINESS_PLAN_TYPES.ONE_PAGE:
              updates.activeSection = ONE_PAGE_SECTIONS.ABOUT_THE_COMPANY;
              updates.activeSubSection = null;

              break;
            case BUSINESS_PLAN_TYPES.FOR_BANK:
              updates.activeSection = FOR_BANK_SECTIONS.OVERVIEW;
              updates.activeSubSection = FOR_BANK_SUB_SECTIONS.FOR_BANK_OVERVIEW.COMPANY;

              break;
            case BUSINESS_PLAN_TYPES.ONE_PAGE_SHORT:
              updates.activeSection = ONE_PAGE_SHORT_SECTIONS.SHORT;
              updates.activeSubSection = null;

              break;
            case BUSINESS_PLAN_TYPES.FOR_INVESTORS:
              updates.activeSection = FOR_INVESTORS_SECTIONS.OVERVIEW;
              updates.activeSubSection = FOR_INVESTORS_SUB_SECTIONS.FOR_INVESTORS_OVERVIEW.COMPANY;

              break;
            default:
              break;
          }

          set(updates);
        },

        businessPlanFocusedField: null,
        setBusinessPlanFocusedField: (focusedField) => set({ businessPlanFocusedField: focusedField }),

        isOpenFullScreenTable: false,
        setisOpenFullScreenTable: (value) => set({ isOpenFullScreenTable: value }),

        businessPlanFocusedFieldId: null,
        setBusinessPlanFocusedFieldId: (focusedFieldId) => set({ businessPlanFocusedFieldId: focusedFieldId }),

        businessPlanFocusedFieldAttribute: null,
        setBusinessPlanFocusedFieldAttribute: (focusedFieldAttribute) =>
          set({ businessPlanFocusedFieldAttribute: focusedFieldAttribute }),

        AIAssistantReadinessPercent: 0,
        setAIAssistantReadinessPercent: (readiness) => set({ AIAssistantReadinessPercent: readiness }),

        businessToolStoreClear: () => {
          get().setBusinessPlanChapters(initialValues);
          get().setBusinessPlan(getDefaultBusinessPlan());
          get().setActiveSection(FOR_BANK_SECTIONS.OVERVIEW);
          get().setActiveSubSection(FOR_BANK_SUB_SECTIONS.FOR_BANK_OVERVIEW.COMPANY);
          get().setAIAssistantReadinessPercent(0);
          get().setBusinessPlanFocusedField(null);
          get().setBusinessPlanFocusedFieldId(null);
          get().setBusinessPlanFocusedFieldAttribute(null);
        },
      }),
      {
        name: 'business-tool',
        version: 0,
        partialize: (state) => ({
          activeSection: state.activeSection,
          activeSubSection: state.activeSubSection,

          businessPlan: state.businessPlan,
          businessPlanTitle: state.businessPlanTitle,
          businessPlanType: state.businessPlanType,
          businessPlanChapters: state.businessPlanChapters,
          isFirstTryToSetBusinessToolTitle: state.isFirstTryToSetBusinessToolTitle,
        }),
      },
    ),
  ),
);
